import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import styles from './formNCMReceitaBruta.module.scss';
import { BrButton, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import { DiefCfemContext } from '../../../../../../../../../../../contexts/DiefCfemContext';
import ErrorMessage from '../../../../../../../../../../../components/errorMessage/errorMessage';
import AtualizarInformacoes from '../../../../../../../../atualizarInformacoes/atualizarInformacoes';
import { ITributos } from '../../../../../../../../../../../interfaces/ITributos';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import { ICodigoNcm } from '../../../../../../../../../../../interfaces/ICodigoNcm';
import { validarReceitaBruta } from './receitaBrutaValidacoes/formNCMReceitaBrutaValidacoes';
import {
  IInformacaoReceitaBrutaEnviar,
  IReceitaBrutaNCMEnviar,
  IReceitaBrutaNCMReceber,
} from '../../../../../../../../../../../interfaces/IReceitaBruta';
import AnmSwitch from '../../../../../../../../../../../components/anmSwitch/anmSwitch';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';

interface IFormNCMReceitaBrutaProps {
  refetchListaNCM: () => void;
  isEdicaoNcm: boolean;
  setIsEdicaoNcm?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchDadosAnteriores?: () => void;
}

const FormNCMReceitaBruta = ({ refetchListaNCM, isEdicaoNcm, setIsEdicaoNcm }: IFormNCMReceitaBrutaProps) => {
  const [tributosAdicionados, setTributosAdicionados] = useState<
    {
      nome: string;
      valor: number;
    }[]
  >([]);

  const [custoBruto, setCustoBruto] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');

  const [codigoNCMSelecionado, setCodigoNCMSelecionado] = useState<string>('');
  const [unidadeMedidaSelecionada, setUnidadeMedidaSelecionada] = useState<string>('');

  const [haRejeito, setHaRejeito] = React.useState<boolean>(false);
  const [haUsoEspecifico, setHaUsoEspecifico] = React.useState<boolean>(false);

  const [haCompraTerceiro, setHaCompraTerceiro] = React.useState<boolean>(false);
  const [quantidadeTerceiro, setQuantidadeTerceiro] = React.useState<string>('');
  const [valorTotalTerceiro, setValorTotalTerceiro] = React.useState<string>('');

  const [isLoadingInfo, setIsLoadingInfo] = React.useState<boolean>(false);
  const [loadingBotao, setLoadingBotao] = React.useState<boolean>(false);

  const [info, setInfo] = React.useState<{ label: string; value: number }[]>([
    { label: 'Deduções (R$)', value: 0 },
    { label: 'Base de Cálculo (R$)', value: 0 },
  ]);

  const {
    getTributosReceitaBruta,
    getNCMReceitaBruta,
    getUnidadesMedidaReceitaBruta,
    postNCMReceitaBruta,
    putNCMReceitaBruta,
    getNCMAdicionadoReceitaBruta,
    postReceberInformacoesReceitaBruta,
  } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();
  const context = useContext(DiefCfemContext);

  const {
    data: tributosPossiveisData,
    isLoading: tributosPossiveisLoading,
    error: tributosPossiveisError,
    refetch: refetchTributosPossiveis,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributosReceitaBruta'],
    queryFn: () => getTributosReceitaBruta(context?.idOperacaoEditar.toString() || ''),
    refetchOnWindowFocus: false,
  });

  const {
    data: ncmData,
    isLoading: ncmLoading,
    error: ncmError,
    refetch: refetchNcm,
  } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['getCodigoNCMReceitaBruta'],
    queryFn: () => getNCMReceitaBruta(),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  const {
    data: unidadeMedidaData,
    isLoading: unidadeMedidaLoading,
    error: unidadeMedidaError,
    refetch: refetchUnidadeMedida,
  } = useQuery<string[], IErrorWithSentryId>({
    queryKey: ['getUnidadeMedidaReceitaBruta'],
    queryFn: () => getUnidadesMedidaReceitaBruta(),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  const {
    data: ncmAdicionadoData,
    isLoading: ncmAdicionadoDataLoading,
    error: ncmAdicionadoDataError,
  } = useQuery<IReceitaBrutaNCMReceber, IErrorWithSentryId>({
    queryKey: ['getNCMAdicionadoReceitaBruta', context?.idReceitaBrutaNCM],
    queryFn: () => getNCMAdicionadoReceitaBruta(context?.idReceitaBrutaNCM ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: isEdicaoNcm && context?.idReceitaBrutaNCM !== -1,
  });

  useEffect(() => {
    if (isEdicaoNcm && ncmAdicionadoData) {
      setCodigoNCMSelecionado(ncmAdicionadoData?.codigoNcm);
      setUnidadeMedidaSelecionada(ncmAdicionadoData?.unidadeMedida);
      setCustoBruto(formatCurrency(ncmAdicionadoData?.valorReceitaBruta));
      setQuantidade(ncmAdicionadoData?.quantidade?.toString());
      setTributosAdicionados(ncmAdicionadoData?.tributosValores);
      setHaRejeito(ncmAdicionadoData?.isRejeito);
      setHaUsoEspecifico(ncmAdicionadoData?.hasUsoEspecifico);
      setHaCompraTerceiro(ncmAdicionadoData?.quantidadeAquisicaoTerceiros !== 0);
      setQuantidadeTerceiro(ncmAdicionadoData?.quantidadeAquisicaoTerceiros?.toString());
      setValorTotalTerceiro(formatCurrency(ncmAdicionadoData?.valorAquisicaoTerceiros));
    }
  }, [isEdicaoNcm, ncmAdicionadoData]);

  const limparCampos = () => {
    setCodigoNCMSelecionado('');
    setUnidadeMedidaSelecionada('');
    setCustoBruto('');
    setQuantidade('');
    setTributosAdicionados([]);
    setHaRejeito(false);
    setHaUsoEspecifico(false);
    setHaCompraTerceiro(false);
    setQuantidadeTerceiro('');
    setValorTotalTerceiro('');
    setInfo([
      { label: 'Deduções (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ]);
  };

  const navegarParaListaNCM = async () => {
    const buttonEdit = document.getElementById('lista-ncm-adicionado');

    if (buttonEdit) {
      window.scrollTo({
        top: buttonEdit.offsetTop,
      });
    }
  };

  const adicionarNCM = async () => {
    if (
      !validarReceitaBruta(
        codigoNCMSelecionado,
        unidadeMedidaSelecionada,
        custoBruto,
        parseFloat(quantidade),
        tributosAdicionados,
        tributosPossiveisData,
        mostrarNotificacao,
        haCompraTerceiro,
        parseFloat(quantidadeTerceiro),
        valorTotalTerceiro,
        haUsoEspecifico
      )
    )
      return false;

    const ncmData: IReceitaBrutaNCMEnviar = {
      idReceitaBrutaVendas: context?.idReceitaBruta || -1,
      codigoNcm: codigoNCMSelecionado,
      unidadeMedida: unidadeMedidaSelecionada,
      quantidade: parseFloat(quantidade),
      valorReceitaBruta: removeFormatCurrency(custoBruto),
      tributosValores: tributosAdicionados,
      valorAquisicaoTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiro) : 0,
      quantidadeAquisicaoTerceiros: haCompraTerceiro ? parseFloat(quantidadeTerceiro) : 0,
      isRejeito: haRejeito,
      hasUsoEspecifico: haUsoEspecifico,
    };

    setLoadingBotao(true);

    if (isEdicaoNcm) {
      const ncmDataComId = {
        ...ncmData,
        id: context?.idReceitaBrutaNCM || -1,
      };
      await putNCMReceitaBruta(ncmDataComId)
        .then(() => {
          limparCampos();
          refetchListaNCM();
          navegarParaListaNCM();
          if (setIsEdicaoNcm) setIsEdicaoNcm(false);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao salvar NCM, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setLoadingBotao(false);
        });
    } else {
      await postNCMReceitaBruta(ncmData)
        .then(() => {
          limparCampos();
          refetchListaNCM();
          navegarParaListaNCM();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao enviar NCM, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setLoadingBotao(false);
        });
    }
  };

  const receberInformacoesBaseCalculo = async () => {
    if (
      !validarReceitaBruta(
        codigoNCMSelecionado,
        unidadeMedidaSelecionada,
        custoBruto,
        parseFloat(quantidade),
        tributosAdicionados,
        tributosPossiveisData,
        mostrarNotificacao,
        haCompraTerceiro,
        parseFloat(quantidadeTerceiro),
        valorTotalTerceiro,
        haUsoEspecifico,
        true
      )
    )
      return false;
    setIsLoadingInfo(true);
    const ncmData: IInformacaoReceitaBrutaEnviar = {
      quantidade: parseFloat(quantidade),
      valorReceitaBruta: removeFormatCurrency(custoBruto),
      tributosValores: tributosAdicionados,
      valorAquisicaoTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiro) : 0,
    };
    await postReceberInformacoesReceitaBruta(ncmData)
      .then((res) => {
        setInfo([
          { label: 'Deduções (R$)', value: res.deducoes },
          { label: 'Base de Cálculo (R$)', value: res.baseCalculo },
        ]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });

        setIsLoadingInfo(false);
      });
  };

  return (
    <div>
      <div className={styles.title}>{isEdicaoNcm ? 'Edite NCM' : 'Adicione um NCM'}</div>
      {ncmAdicionadoDataError && isEdicaoNcm && <ErrorMessage errorMessage={''} error={ncmAdicionadoDataError} />}
      {isEdicaoNcm && ncmAdicionadoDataLoading ? (
        <BrLoading />
      ) : (
        <div className={styles.content}>
          <div className={styles.form}>
            <Row>
              <Col>
                <span className={styles.label}>Código NCM</span>
                {ncmLoading ? (
                  <BrLoading />
                ) : (
                  <BrSelect
                    data-testid='codigo-ncm'
                    key={codigoNCMSelecionado}
                    className={styles.select}
                    placeholder='Selecione um NCM...'
                    value={codigoNCMSelecionado}
                    onChange={(value: string) => {
                      setCodigoNCMSelecionado(value);
                    }}
                    options={
                      ncmData?.map((item: ICodigoNcm) => ({
                        label: `${item.codigo} - ${item.descricao}`,
                        value: item.codigo,
                      })) || []
                    }
                    disabled={false}
                  />
                )}
                {ncmError && (
                  <div className={styles.errorContainer}>
                    <span className={styles.errorText}>Não foi possível atualizar a lista de NCM</span>
                    <BrButton size='small' onClick={() => refetchNcm()}>
                      (Tentar novamente)
                    </BrButton>
                  </div>
                )}
              </Col>
              <Col>
                <span className={styles.label}>Unidade de medida</span>
                {unidadeMedidaLoading ? (
                  <BrLoading />
                ) : (
                  <BrSelect
                    data-testid='unidade-medida'
                    key={unidadeMedidaSelecionada}
                    className={styles.select}
                    placeholder='Selecione uma unidade de medida...'
                    value={unidadeMedidaSelecionada}
                    onChange={(value: string) => {
                      setUnidadeMedidaSelecionada(value);
                    }}
                    options={
                      unidadeMedidaData?.map((item) => {
                        return { label: item, value: item };
                      }) || []
                    }
                  />
                )}
                {unidadeMedidaError && (
                  <div className={styles.errorContainer}>
                    <span className={styles.errorText}>Não foi possível atualizar a lista de unidades de medida</span>
                    <BrButton size='small' onClick={() => refetchUnidadeMedida()}>
                      (Tentar novamente)
                    </BrButton>
                  </div>
                )}
              </Col>
              <Col></Col>
            </Row>
            <Row>
              <Col>
                <span className={styles.label}>Receita Bruta de Venda (R$)</span>
                <BrInput
                  data-testid='receita-bruta-input'
                  autoComplete='off'
                  numeric={{
                    prefix: 'R$ ',
                    thousandSeparator: '.',
                    decimalScale: 2,
                    fixedDecimalScale: false,
                  }}
                  required
                  onChange={(e) => setCustoBruto(e.target.value)}
                  value={custoBruto}
                />
              </Col>
              <Col>
                <span className={styles.label}>Quantidade</span>
                <BrInput
                  data-testid='quantidade-input'
                  autoComplete='off'
                  numeric={{
                    decimalScale: 2,
                    fixedDecimalScale: false,
                  }}
                  onChange={(e) => {
                    setQuantidade(e.target.value.replace(',', '.'));
                  }}
                  value={quantidade}
                />
              </Col>
              <Col className={styles.dataContainer}>
                <span className={styles.label}>Preço Médio (R$)</span>
                <span>
                  {custoBruto && quantidade
                    ? formatCurrency(removeFormatCurrency(custoBruto) / parseFloat(quantidade))
                    : '-'}
                </span>
              </Col>
            </Row>
          </div>
          {tributosPossiveisLoading ? (
            <BrLoading />
          ) : (
            <SecaoDeducoes
              key={codigoNCMSelecionado}
              deducoesPossiveis={tributosPossiveisData}
              deducoesAdicionadas={tributosAdicionados}
              setDeducoesAdicionadas={setTributosAdicionados}
              semMargem
              valorReceitaBruta={custoBruto}
              isApresentarSecaoCompraTerceiros
              haCompraTerceiro={haCompraTerceiro}
              setHaCompraTerceiro={setHaCompraTerceiro}
              quantidadeCompraTerceiro={quantidadeTerceiro}
              setQuantidadeCompraTerceiro={setQuantidadeTerceiro}
              valorCompraTerceiro={valorTotalTerceiro}
              setValorCompraTerceiro={setValorTotalTerceiro}
            />
          )}
          {tributosPossiveisError && (
            <div className={styles.errorContainer}>
              <span className={styles.errorText}>Não foi possível atualizar a lista de tributos</span>
              <BrButton size='small' onClick={() => refetchTributosPossiveis()}>
                (Tentar novamente)
              </BrButton>
            </div>
          )}
          <div className={styles.otherContainer}>
            <div className={styles.atualizarInformacoesContainerFull}>
              <AtualizarInformacoes
                onClickFunction={() => receberInformacoesBaseCalculo()}
                info={info}
                isLoading={isLoadingInfo}
              />
            </div>
          </div>
          <div className={styles.form}>
            <AnmSwitch
              switchDataTestId='switch-rejeito'
              label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias
                  produtivas?'
              value={haRejeito}
              setValue={setHaRejeito}
              tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
            />
            <AnmSwitch
              switchDataTestId='switch-uso-especifico'
              label='A substância possui uso específico?'
              value={haUsoEspecifico}
              setValue={setHaUsoEspecifico}
              tooltipText='Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil ou se enquadrar na categoria de rochas ornamentais, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei nº 8.001/1990.'
            />
          </div>
          <div className={styles.buttonContainer}>
            {isEdicaoNcm === true && (
              <BrButton
                data-testid='btn-salvar-ncm'
                secondary
                size='small'
                icon='fas fa-save'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Salvar NCM
              </BrButton>
            )}
            {isEdicaoNcm === false && (
              <BrButton
                data-testid='btn-incluir-ncm'
                secondary
                size='small'
                icon='fas fa-plus'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Incluir NCM
              </BrButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormNCMReceitaBruta;
